import {PIXI} from '@colonist/pixi'
import {GameColor} from '../../GameColor'
import {TimeUtils} from '../../../../../shared/utils/TimeUtils'
import {SectionView} from '../../ui_canvas_engine/SectionView'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {UIGameMapView} from './UIGameMapView'
import {UIGameMapDiceProbabilityView} from './UIGameMapDiceProbabilityView'
import {UIGameAnimation} from '../UIGameEventController'
import {UIGameOpacityAnimation} from '../../ui_canvas_engine/UIGameOpacityAnimation'
import {UIGameManager} from '../UIGameManager'
import {HexFace, Point} from '@colonist/game-mechanic-validators'

export class UIGameMapTileView extends SectionView implements UIGameAnimation {
    private readonly mapView: UIGameMapView
    private readonly uiGameManager: UIGameManager
    private readonly opacityAnimation: UIGameOpacityAnimation
    private whiteSurrounding: PIXI.Graphics | undefined

    hexFace: HexFace
    tileSpriteView: SpriteView
    diceNumber: number | undefined
    probabilityView: UIGameMapDiceProbabilityView | undefined

    constructor(uiGameManager: UIGameManager, tileHex: HexFace, x: number, y: number, w: number, h: number, texture: PIXI.Texture, container: PIXI.Container) {
        const removePadding = true
        super(x, y, w, h, container, removePadding)
        this.mapView = uiGameManager.mapController.mapView
        this.uiGameManager = uiGameManager

        const smallScalingToOverlapGapsBetweenTiles = 1.015
        const tileWidth = this.width * smallScalingToOverlapGapsBetweenTiles
        const tileHeight = this.height * smallScalingToOverlapGapsBetweenTiles
        const xOffset = (this.width - tileWidth) / 2
        const yOffset = (this.height - tileHeight) / 2

        this.tileSpriteView = new SpriteView(xOffset, yOffset, tileWidth, tileHeight, this, texture)

        this.hexFace = tileHex
        this.zIndex = this.mapView.layers.mapTiles
        this.opacityAnimation = new UIGameOpacityAnimation()
    }

    onGameTick(deltaTime: number, elapsedMS: number) {
        if(this.whiteSurrounding == undefined) return
        this.opacityAnimation.animateOpacity(this.whiteSurrounding)
    }

    highlightTileForTime(highlightTimeInSeconds: number) {
        this.highlight()
        const timeToHighlight = highlightTimeInSeconds * TimeUtils.MILLISECONDS
        setTimeout(() => {
            this.resetHighlight()
        }, timeToHighlight)
    }

    highlight() {
        this.tileSpriteView.tint = GameColor.getColorHexValue(GameColor.Color.PrimaryBlue3)
    }

    highlightWithWhiteSurrounding() {
        this.tileSpriteView.tint = GameColor.getColorHexValue(GameColor.Color.GrayScale20)
        this.uiGameManager.eventController.registerUIGameAnimation(this)
        this.drawWhiteSurrounding()
    }

    private drawWhiteSurrounding() {
        const radius = this.mapView.hexagonHeight / 2 - this.mapView.hexagonHeight * 0.01
        const startX = this.mapView.hexagonWidth / 2
        const startY = this.mapView.hexagonHeight / 2

        const hexagonalOutline = new PIXI.Graphics()
        const width = this.mapView.hexagonHeight * 0.02
        const color = GameColor.getColorHexValue(GameColor.Color.GrayScale25)
        const alpha = 0.9

        const initialAngle = Math.PI / 6
        hexagonalOutline.lineStyle(width, color, alpha)
        hexagonalOutline.moveTo(startX + radius * Math.cos(-initialAngle),
            startY + radius * Math.sin(-initialAngle))

        const tileEdgeCount = 6
        for(let i = 1; i <= tileEdgeCount; i++) {
            const angle = (Math.PI / 3) * i - Math.PI / 6
            hexagonalOutline.lineTo(startX + radius * Math.cos(angle),
                startY + radius * Math.sin(angle))
        }

        hexagonalOutline.closePath()
        this.whiteSurrounding = hexagonalOutline
        this.addChild(hexagonalOutline)
    }

    resetHighlight() {
        this.tileSpriteView.tint = GameColor.getColorHexValue(GameColor.Color.White)
    }

    resetHighlightWithWhiteSurrounding() {
        if(this.whiteSurrounding == undefined) return

        this.resetHighlight()
        this.whiteSurrounding?.destroy()
        this.whiteSurrounding = undefined
        this.uiGameManager.eventController.unregisterUIGameAnimation(this)
    }

    drawProbability(diceNumber: number) {
        this.removeDiceProbability()
        this.diceNumber = diceNumber
        const probabilityTexture = UIGameSpriteHandler.getProbabilityImage(diceNumber)
        if(probabilityTexture == undefined) return

        const probRadius = this.mapView.robberRadius

        const x = this.width / 2 - probRadius / 2
        const y = this.height / 2 - probRadius / 2 - probRadius / 8 + this.mapView.hexagonHeight / 5
        this.probabilityView = new UIGameMapDiceProbabilityView(x, y, probRadius, probRadius, this.hexFace, this, probabilityTexture)
        this.probabilityView.zIndex = this.mapView.layers.mapTiles
    }

    private removeDiceProbability() {
        if(this.probabilityView == undefined) return
        if(this.probabilityView.parent != undefined) this.probabilityView.parent.removeChild(this.probabilityView)
    }

    getDiceProbabilityMapPoint(): Point | undefined {
        if(this.probabilityView == undefined) return undefined
        return new Point(this.x + this.probabilityView.x, this.y + this.probabilityView.y)
    }
}
